import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, Search, RefreshCw } from 'lucide-react';
import { Input } from '@client/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import { USAGE_METRICS_CONFIG } from '@shared/config/usageMetricsConfig';
import { formatMetricValue, buildMetricTooltip } from '@/utils/metricFormatting';
import type { NylPilotAgentMetrics, NylPilotMetricsResponse } from '@shared';
import type {
  DateFilterState,
  DatePreset,
  MetricsOrganization,
  WizardAdvisorUsageData,
} from '@client/features/wizard-advisors-metrics/types/metrics-type';
import { format, parseISO } from 'date-fns';
import { formatDuration } from '@client/features/wizard-advisors-metrics/components/overview-tab';
import {
  MetricsAppliedFilters,
  MetricsDateFilter,
  MetricsOrganizationFilter,
} from '@client/features/wizard-advisors-metrics/components/metrics-date-filter';
import { useDateFilter } from '@client/features/wizard-advisors-metrics/hooks/use-date-filter';
import { useNylMetricsApi } from '@client/features/wizard-advisors-metrics/hooks/api/use-advisor-metrics-api';
import { isNylMetricsAdvisor } from '@client/features/wizard-advisors-metrics/hooks/use-advisor-metrics';
import { Button, Progress } from '@design-system';
import { TablePaginationControls } from '@/components/TablePaginationControls';

const USAGE_TABLE_DEFAULT_PAGE_SIZE = 25;

const WIZARD_USAGE_METRIC_DESCRIPTIONS: Partial<Record<keyof UsageMetrics, string>> = {
  totalInvitedClients:
    'Clients who have received an invitation but have not yet completed their intake.',
  totalClientsWithSurveysCompleted: 'Clients who completed all required intake questionnaires.',
  totalClientPolicies:
    'The total number of policies advisors added to client plans for comparison and planning.',
  totalMonthlyRecurringPremiums:
    'The combined monthly premium amount for policies with payments that continue for life.',
  totalFixedPremiums:
    'The combined premium amount for policies paid in one payment or over a set number of years.',
  totalClientsWithFinancePlans:
    'Clients for whom an advisor created a long-term care funding plan.',
};

const ENHANCED_USAGE_METRICS = USAGE_METRICS_CONFIG.filter(
  metric =>
    !['totalClientsWithApplicationsStarted', 'totalApplicationsSubmitted'].includes(metric.key),
).map(metric => ({
  ...metric,
  description: WIZARD_USAGE_METRIC_DESCRIPTIONS[metric.key] ?? metric.description,
}));
const USAGE_SUMMARY_METRIC_KEYS = new Set<keyof UsageMetrics>([
  'totalInvitedClients',
  'totalClientsWithSurveysCompleted',
  'totalClientPolicies',
  'totalMonthlyRecurringPremiums',
  'totalFixedPremiums',
]);
const USAGE_SUMMARY_METRICS = ENHANCED_USAGE_METRICS.filter(metric =>
  USAGE_SUMMARY_METRIC_KEYS.has(metric.key),
);

interface AdvisorUsageTableProps {
  advisors: WizardAdvisorUsageData[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
  nylMetrics?: NylPilotMetricsResponse;
  isNylMetricsLoading?: boolean;
  dateFilter?: DateFilterState;
  organization?: MetricsOrganization;
  onOrganizationChange?: (organization: MetricsOrganization) => void;
  onDatePresetChange?: (preset: DatePreset) => void;
  onCustomDatesChange?: (startDate: Date, endDate: Date) => void;
  onExportNylClients?: () => void;
  isNylExporting?: boolean;
  selectedAdvisorId?: string | null;
  onSelectedAdvisorChange?: (advisorId: string | null) => void;
  calculatedMetrics?: UsageMetrics;
  filterKey?: string | null;
  onClearFilter?: () => void;
}

export function EnhancedAdvisorUsageTable({
  advisors,
  onRefresh,
  isRefreshing,
  nylMetrics,
  isNylMetricsLoading = false,
  dateFilter,
  organization,
  onOrganizationChange,
  onDatePresetChange,
  onCustomDatesChange,
  onExportNylClients,
  isNylExporting = false,
  selectedAdvisorId,
  onSelectedAdvisorChange,
  calculatedMetrics,
  filterKey,
  onClearFilter,
}: AdvisorUsageTableProps) {
  const [internalSelectedAdvisorId, setInternalSelectedAdvisorId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const detailDateFilter = useDateFilter();
  const activeSelectedAdvisorId =
    selectedAdvisorId === undefined ? internalSelectedAdvisorId : selectedAdvisorId;
  const selectAdvisor = onSelectedAdvisorChange ?? setInternalSelectedAdvisorId;

  const openAdvisor = (advisorId: string) => {
    detailDateFilter.resetFilter();
    selectAdvisor(advisorId);
  };

  const filteredAdvisors = advisors.filter(advisor => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      advisor.advisorName.toLowerCase().includes(search) ||
      advisor.advisorEmail.toLowerCase().includes(search) ||
      advisor.organizationName.toLowerCase().includes(search)
    );
  });

  const selectedAdvisor = advisors.find(advisor => advisor.advisorId === activeSelectedAdvisorId);
  const detailMetricsQuery = useNylMetricsApi({
    enabled: Boolean(selectedAdvisor),
    advisorId: selectedAdvisor?.advisorId,
    scope: 'all',
    staleTimeMs: 0,
    ...(detailDateFilter.dateFilter.preset === 'allTime'
      ? {}
      : {
          from: format(detailDateFilter.dateFilter.startDate, 'yyyy-MM-dd'),
          to: format(detailDateFilter.dateFilter.endDate, 'yyyy-MM-dd'),
        }),
  });
  const detailRangeContext = `${detailDateFilter.dateFilter.preset}:${format(
    detailDateFilter.dateFilter.startDate,
    'yyyy-MM-dd',
  )}:${format(detailDateFilter.dateFilter.endDate, 'yyyy-MM-dd')}`;
  const detailAllTimeMetricsQuery = useNylMetricsApi({
    enabled: Boolean(selectedAdvisor) && detailDateFilter.dateFilter.preset !== 'allTime',
    advisorId: selectedAdvisor?.advisorId,
    scope: 'all',
    staleTimeMs: 0,
    retainPreviousData: true,
    queryKeyContext: `advisor-comparison:${detailRangeContext}`,
  });
  const detailAllTimeMetrics =
    detailDateFilter.dateFilter.preset === 'allTime'
      ? detailMetricsQuery.data
      : detailAllTimeMetricsQuery.data;
  const selectedAgent = selectedAdvisor
    ? !detailMetricsQuery.isFetching
      ? detailMetricsQuery.data?.agents.find(agent => agent.advisorId === selectedAdvisor.advisorId)
      : undefined
    : undefined;
  const selectedAllTimeAgent = selectedAdvisor
    ? detailAllTimeMetrics?.agents.find(agent => agent.advisorId === selectedAdvisor.advisorId)
    : undefined;

  if (selectedAdvisor) {
    return (
      <div className="space-y-4">
        <WizardAdvisorMetricsPanel
          advisor={selectedAdvisor}
          agent={selectedAgent}
          allTimeAgent={selectedAllTimeAgent}
          dateFilter={detailDateFilter.dateFilter}
          onDatePresetChange={detailDateFilter.setPreset}
          onCustomDatesChange={detailDateFilter.setCustomDates}
          onBack={() => selectAdvisor(null)}
        />
      </div>
    );
  }

  return (
    <WizardAdvisorUsageList
      advisors={filteredAdvisors}
      searchText={searchText}
      onSearchTextChange={setSearchText}
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
      isNylMetricsLoading={isNylMetricsLoading}
      nylMetrics={nylMetrics}
      dateFilter={dateFilter}
      organization={organization}
      onOrganizationChange={onOrganizationChange}
      onDatePresetChange={onDatePresetChange}
      onCustomDatesChange={onCustomDatesChange}
      onExportNylClients={onExportNylClients}
      isNylExporting={isNylExporting}
      onSelectAdvisor={openAdvisor}
      calculatedMetrics={calculatedMetrics}
      filterKey={filterKey}
      onClearFilter={onClearFilter}
    />
  );
}

function WizardAdvisorUsageList({
  advisors,
  searchText,
  onSearchTextChange,
  onRefresh,
  isRefreshing,
  isNylMetricsLoading,
  nylMetrics,
  dateFilter,
  organization,
  onOrganizationChange,
  onDatePresetChange,
  onCustomDatesChange,
  onExportNylClients,
  isNylExporting,
  onSelectAdvisor,
  calculatedMetrics,
  filterKey,
  onClearFilter,
}: {
  advisors: WizardAdvisorUsageData[];
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isNylMetricsLoading: boolean;
  nylMetrics?: NylPilotMetricsResponse;
  dateFilter?: DateFilterState;
  organization?: MetricsOrganization;
  onOrganizationChange?: (organization: MetricsOrganization) => void;
  onDatePresetChange?: (preset: DatePreset) => void;
  onCustomDatesChange?: (startDate: Date, endDate: Date) => void;
  onExportNylClients?: () => void;
  isNylExporting: boolean;
  onSelectAdvisor: (advisorId: string) => void;
  calculatedMetrics?: UsageMetrics;
  filterKey?: string | null;
  onClearFilter?: () => void;
}) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(USAGE_TABLE_DEFAULT_PAGE_SIZE);
  const visibleAdvisorIds = new Set(advisors.map(advisor => advisor.advisorId));
  const activeAdvisorsInPeriod = nylMetrics
    ? nylMetrics.agents.filter(
        agent => visibleAdvisorIds.has(agent.advisorId) && agent.activeDays > 0,
      ).length
    : '—';
  const periodLoginsByAdvisorId = new Map(
    nylMetrics?.agents.map(agent => [agent.advisorId, agent.logins]) ?? [],
  );
  const pageCount = Math.max(1, Math.ceil(advisors.length / pageSize));
  const firstRow = advisors.length === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min(firstRow + pageSize - 1, advisors.length);
  const paginatedAdvisors = advisors.slice(firstRow - 1, lastRow);
  const stickyControlsRef = useRef<HTMLDivElement>(null);
  const [stickyControlsHeight, setStickyControlsHeight] = useState(0);

  useEffect(() => {
    setPageIndex(0);
  }, [searchText, organization, filterKey]);

  useEffect(() => {
    setPageIndex(currentPageIndex => Math.min(currentPageIndex, pageCount - 1));
  }, [pageCount]);

  useEffect(() => {
    if (!stickyControlsRef.current) return;

    const controls = stickyControlsRef.current;
    const updateControlsHeight = () => setStickyControlsHeight(controls.offsetHeight);

    updateControlsHeight();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateControlsHeight);
    observer.observe(controls);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="space-y-4"
      style={
        {
          '--usage-sticky-header-top': `calc(var(--metrics-sticky-table-top) + ${stickyControlsHeight}px)`,
        } as React.CSSProperties
      }
    >
      <div
        ref={stickyControlsRef}
        data-testid="usage-sticky-controls"
        className="z-30 space-y-4 bg-white pb-2 lg:sticky lg:top-[var(--metrics-sticky-table-top)]"
      >
        <MetricsAppliedFilters
          filterKey={filterKey}
          onRemoveFilter={onClearFilter}
        />
        <div
          data-testid="usage-list-controls"
          className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center"
        >
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              type="text"
              placeholder="Search advisors..."
              value={searchText}
              onChange={event => onSearchTextChange(event.target.value)}
              className="pl-10 pr-9"
            />
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                aria-label="Refresh advisor usage"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>

          <div
            className={
              organization !== undefined && onOrganizationChange
                ? 'flex w-full items-center gap-2 lg:contents lg:w-auto'
                : 'w-full lg:contents lg:w-auto'
            }
          >
            {organization !== undefined && onOrganizationChange && (
              <MetricsOrganizationFilter
                organization={organization}
                onOrganizationChange={onOrganizationChange}
                idPrefix="usage-metrics"
                compact
              />
            )}

            {dateFilter && onDatePresetChange && onCustomDatesChange && (
              <MetricsDateFilter
                dateFilter={dateFilter}
                onPresetChange={onDatePresetChange}
                onCustomDatesChange={onCustomDatesChange}
                idPrefix="usage-list"
                compact
              />
            )}
          </div>

          <div className="ml-auto flex items-center gap-3">
            {isNylMetricsLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-500" />}
            {organization === 'nyl' && onExportNylClients && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onExportNylClients}
                disabled={isNylExporting}
              >
                {isNylExporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {isNylExporting ? 'Preparing CSV…' : 'NYL Clients Export'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div
        data-testid="usage-summary-tiles"
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <TooltipProvider>
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4 lg:grid-cols-7">
            <SummaryMetric
              label="Active Advisors"
              value={activeAdvisorsInPeriod}
              description="Advisors in the current list who used Waterlily at least once during the selected date range."
            />
            {USAGE_SUMMARY_METRICS.map(metric => (
              <SummaryMetric
                key={metric.key}
                label={metric.label}
                value={isNylMetricsLoading ? '—' : (calculatedMetrics?.[metric.key] ?? '—')}
                description={metric.description}
                format={metric.format}
              />
            ))}
          </div>
        </TooltipProvider>
      </div>

      {isNylMetricsLoading ? (
        <div className="flex min-h-48 items-center justify-center rounded-lg border border-gray-200 bg-white">
          <Loader2
            className="h-6 w-6 animate-spin text-gray-500"
            aria-label="Loading advisor activity"
          />
        </div>
      ) : (
        <>
          <div
            data-testid="mobile-advisor-list"
            className="space-y-3 lg:hidden"
          >
            {advisors.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-12 text-center text-gray-700">
                {searchText
                  ? 'No advisors found matching your search.'
                  : 'No advisor data available.'}
              </div>
            ) : (
              paginatedAdvisors.map(advisor => (
                <MobileAdvisorCard
                  key={advisor.advisorId}
                  advisor={advisor}
                  logins={periodLoginsByAdvisorId.get(advisor.advisorId) ?? (nylMetrics ? 0 : '—')}
                  onSelectAdvisor={onSelectAdvisor}
                />
              ))
            )}
          </div>

          <div
            data-testid="desktop-advisor-table"
            className="hidden rounded-lg border border-gray-200 bg-white lg:block"
          >
            <div
              data-testid="usage-advisors-table__sticky-header"
              className="z-20 h-0 overflow-visible bg-white lg:sticky lg:top-[var(--usage-sticky-header-top)]"
            >
              <div className="absolute -inset-x-px top-0 bg-white px-px">
                <div className="overflow-hidden rounded-t-lg bg-white">
                  <Table className="table-fixed">
                    <UsageAdvisorTableHeader />
                  </Table>
                </div>
              </div>
            </div>
            <Table className="table-fixed">
              <UsageAdvisorTableHeader layoutOnly />
              <TableBody>
                {advisors.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-12 text-center text-gray-700"
                    >
                      {searchText
                        ? 'No advisors found matching your search.'
                        : 'No advisor data available.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAdvisors.map(advisor => (
                    <TableRow
                      key={advisor.advisorId}
                      className="cursor-pointer transition-colors hover:bg-gray-100"
                      onClick={() => onSelectAdvisor(advisor.advisorId)}
                    >
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="bg-primary-50 text-primary-700 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                            {advisorInitials(advisor.advisorName)}
                          </div>
                          <span className="truncate text-sm font-medium text-gray-900">
                            {advisor.advisorName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-gray-900">
                        <span
                          className="block truncate"
                          title={advisor.organizationName}
                        >
                          {advisor.organizationName}
                        </span>
                      </TableCell>
                      <TableCell className="truncate text-gray-900">{advisor.accountAge}</TableCell>
                      <TableCell className="text-gray-700">
                        <span
                          className="block truncate"
                          title={advisor.advisorEmail}
                        >
                          {advisor.advisorEmail}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-900">
                        {periodLoginsByAdvisorId.get(advisor.advisorId) ?? (nylMetrics ? 0 : '—')}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        ${(advisor.usageMetrics.totalFixedPremiums ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        {clientsCreated(advisor)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={event => {
                            event.stopPropagation();
                            onSelectAdvisor(advisor.advisorId);
                          }}
                        >
                          View metrics
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <TablePaginationControls
            totalRows={advisors.length}
            firstRow={firstRow}
            lastRow={lastRow}
            pageSize={pageSize}
            canPreviousPage={pageIndex > 0}
            canNextPage={pageIndex < pageCount - 1}
            onPreviousPage={() => setPageIndex(currentPageIndex => currentPageIndex - 1)}
            onNextPage={() => setPageIndex(currentPageIndex => currentPageIndex + 1)}
            onPageSizeChange={nextPageSize => {
              setPageSize(nextPageSize);
              setPageIndex(0);
            }}
            responsive
            alwaysShow
          />
        </>
      )}
    </div>
  );
}

function UsageAdvisorTableHeader({ layoutOnly = false }: { layoutOnly?: boolean }) {
  return (
    <TableHeader
      aria-hidden={layoutOnly || undefined}
      className={
        layoutOnly ? 'invisible' : 'border-primary-200 bg-primary-50 [&_th]:bg-primary-50 border-b'
      }
    >
      <TableRow>
        <TableHead className="w-[16%]">Advisor Name</TableHead>
        <TableHead className="w-[15%]">Partner</TableHead>
        <TableHead className="w-[10%]">Account Age</TableHead>
        <TableHead className="w-[19%]">Email</TableHead>
        <TableHead className="w-[9%]">Total Logins</TableHead>
        <TableHead className="w-[9%] text-right">Fixed Premiums</TableHead>
        <TableHead className="w-[9%] text-right">Clients Created</TableHead>
        <TableHead className="w-[13%] text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function MobileAdvisorCard({
  advisor,
  logins,
  onSelectAdvisor,
}: {
  advisor: WizardAdvisorUsageData;
  logins: number | string;
  onSelectAdvisor: (advisorId: string) => void;
}) {
  return (
    <article
      data-testid={`mobile-advisor-card-${advisor.advisorId}`}
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="bg-primary-50 text-primary-700 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
            {advisorInitials(advisor.advisorName)}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-gray-900">{advisor.advisorName}</p>
            <p className="truncate text-sm text-gray-700">{advisor.advisorEmail}</p>
            {advisor.organizationName && (
              <p className="mt-1 truncate text-sm text-gray-700">{advisor.organizationName}</p>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto shrink-0 px-0 py-1 text-xs"
          onClick={() => onSelectAdvisor(advisor.advisorId)}
        >
          View metrics
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3">
        <div>
          <dt className="text-xs font-medium text-gray-600">Total Logins</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">{logins}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-gray-600">Clients Created</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">{clientsCreated(advisor)}</dd>
        </div>
      </dl>
    </article>
  );
}

function SummaryMetric({
  label,
  value,
  description,
  format: valueFormat,
}: {
  label: string;
  value: string | number;
  description?: string;
  format?: 'number' | 'currency' | 'percentage';
}) {
  const { formattedValue, fullValue } = formatMetricValue(value, valueFormat);
  const tooltipText = buildMetricTooltip(fullValue, description);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex h-full cursor-help flex-col">
          <p className="min-h-9 text-sm leading-tight text-gray-700">{label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{formattedValue}</p>
        </div>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}

function WizardAdvisorMetricsPanel({
  advisor,
  agent,
  allTimeAgent,
  dateFilter,
  onDatePresetChange,
  onCustomDatesChange,
  onBack,
}: {
  advisor: WizardAdvisorUsageData;
  agent?: NylPilotAgentMetrics;
  allTimeAgent?: NylPilotAgentMetrics;
  dateFilter?: DateFilterState;
  onDatePresetChange?: (preset: DatePreset) => void;
  onCustomDatesChange?: (startDate: Date, endDate: Date) => void;
  onBack: () => void;
}) {
  const usage = advisor.usageMetrics;
  const enrolledAt = agent?.enrolledAt ?? advisor.enrolledAt;
  const firstClientAddedAt = advisor.firstClientAddedAt;
  const lastLoginAt = advisor.lastLoginAt;
  const allTimeSelected = !dateFilter || dateFilter.preset === 'allTime';
  const periodLabel = dateFilter ? datePresetLabel(dateFilter) : 'Selected period';
  const resolvedAllTimeAgent = allTimeAgent ?? (allTimeSelected ? agent : undefined);
  const allTimeValue = (key: ActivityMetricKey): number | undefined => {
    const storedAllTimeValue = resolvedAllTimeAgent?.[key];
    const selectedPeriodValue = allTimeSelected ? undefined : agent?.[key];
    if (storedAllTimeValue === undefined) return undefined;
    return selectedPeriodValue === undefined
      ? storedAllTimeValue
      : Math.max(storedAllTimeValue, selectedPeriodValue);
  };
  const periodValue = (key: ActivityMetricKey): number | undefined =>
    allTimeSelected ? allTimeValue(key) : agent?.[key];
  const activityRows: MetricComparisonRowDefinition[] = [
    {
      type: 'tracked',
      key: 'logins',
      label: 'Logins',
      description:
        'The number of signed-in sessions started by this advisor during the selected date range. Each session is counted once.',
    },
    {
      type: 'tracked',
      key: 'activeDays',
      label: 'Active days',
      description:
        'The number of different days this advisor used Waterlily during the selected date range.',
    },
    ...(isNylMetricsAdvisor(advisor)
      ? [
          {
            type: 'tracked' as const,
            key: 'activeSeconds' as const,
            label: 'Active time',
            description:
              'The time this advisor actively used Waterlily during the selected date range. Time when the app was idle or not visible is not counted.',
          },
        ]
      : []),
  ];
  const periodMetricGroups: MetricComparisonGroup[] = [
    {
      title: 'Activity',
      rows: activityRows,
    },
    {
      title: 'Client outcomes',
      rows: [
        {
          type: 'tracked',
          key: 'invitationsSent',
          label: 'Invitations sent',
          description: 'The number of client invitations successfully sent by this advisor.',
        },
        usageMetricRow('totalInvitedClients'),
        {
          type: 'tracked',
          key: 'questionnairesCompleted',
          label: 'Questionnaires completed',
          description:
            'The number of clients who completed their first intake questionnaire with this advisor.',
        },
        {
          type: 'tracked',
          key: 'planningOutputsGenerated',
          label: 'Prediction summaries generated',
          description:
            "The number of LTC prediction summaries created or regenerated for this advisor's clients. Regenerating a summary counts again.",
        },
        {
          type: 'tracked',
          key: 'policiesAdded',
          label: 'Policies added',
          description:
            'The number of policies this advisor added to client plans for comparison and planning.',
        },
        usageMetricRow('totalClientsWithFinancePlans'),
      ],
    },
    {
      title: 'Premiums',
      rows: [usageMetricRow('totalMonthlyRecurringPremiums'), usageMetricRow('totalFixedPremiums')],
    },
  ];
  return (
    <div className="space-y-6">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onBack}
      >
        <ChevronLeft className="h-4 w-4" />
        Back to list
      </Button>

      <div className="flex flex-col items-stretch gap-5 xl:flex-row xl:items-start">
        <aside className="w-full rounded-lg border border-gray-200 bg-white p-5 xl:max-w-sm">
          <div className="flex items-center gap-3">
            <div className="bg-primary-50 text-primary-700 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
              {advisorInitials(advisor.advisorName)}
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-lg font-semibold text-gray-900">
                {advisor.advisorName}
              </h3>
              <p className="truncate text-sm text-gray-700">{advisor.advisorEmail}</p>
            </div>
          </div>

          <div className="mt-5 space-y-4 border-t border-gray-200 pt-4">
            <ProfileValue
              label="Partner"
              value={advisor.organizationName || 'Unknown'}
            />
            <ProfileValue
              label="Account age"
              value={advisor.accountAge}
            />
            <ProfileValue
              label="Enrolled"
              value={enrolledAt ? format(parseISO(enrolledAt), 'MMM d, yyyy') : 'Unknown'}
            />
            <ProfileValue
              label="First client added"
              value={
                firstClientAddedAt ? format(parseISO(firstClientAddedAt), 'MMM d, yyyy') : 'Not yet'
              }
            />
            <ProfileValue
              label="Last login"
              value={lastLoginAt ? format(parseISO(lastLoginAt), 'MMM d, yyyy, h:mm a') : 'Not yet'}
            />
            <div>
              <div className="flex items-center justify-between gap-3 text-xs text-gray-700">
                <span>Onboarding Progress</span>
                <span className="font-medium text-gray-900">
                  {usage.advisorOnboardingPercentage}%
                </span>
              </div>
              <Progress
                value={usage.advisorOnboardingPercentage}
                className="mt-2 h-2"
              />
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1 space-y-5">
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">Advisor metrics</h4>
              {dateFilter && onDatePresetChange && onCustomDatesChange && (
                <MetricsDateFilter
                  dateFilter={dateFilter}
                  onPresetChange={onDatePresetChange}
                  onCustomDatesChange={onCustomDatesChange}
                  idPrefix="advisor-detail"
                  compact
                />
              )}
            </div>
            <div
              className={`grid items-center border-b border-gray-200 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-700 ${
                allTimeSelected
                  ? 'grid-cols-[minmax(0,1fr)_130px]'
                  : 'grid-cols-[minmax(0,1fr)_130px_130px]'
              }`}
            >
              <span>Metric</span>
              {!allTimeSelected && <span className="text-right">{periodLabel}</span>}
              <span className="text-right">All time</span>
            </div>
            <TooltipProvider>
              {periodMetricGroups.map(group => (
                <React.Fragment key={group.title}>
                  <div className="border-b border-gray-200 bg-gray-50 px-5 py-2 text-xs font-semibold text-gray-900">
                    {group.title}
                  </div>
                  {group.rows.map(row => (
                    <MetricComparisonRow
                      key={`${row.type}-${row.key}`}
                      row={row}
                      periodValue={row.type === 'tracked' ? periodValue(row.key) : undefined}
                      allTimeValue={row.type === 'tracked' ? allTimeValue(row.key) : usage[row.key]}
                      allTimeSelected={allTimeSelected}
                    />
                  ))}
                </React.Fragment>
              ))}
            </TooltipProvider>
          </section>
        </div>
      </div>
    </div>
  );
}

type ActivityMetricKey =
  | 'logins'
  | 'activeDays'
  | 'activeSeconds'
  | 'invitationsSent'
  | 'questionnairesCompleted'
  | 'planningOutputsGenerated'
  | 'policiesAdded';

interface TrackedMetricComparisonRowDefinition {
  type: 'tracked';
  key: ActivityMetricKey;
  label: string;
  description: string;
}

interface AllTimeMetricComparisonRowDefinition {
  type: 'allTime';
  key: keyof UsageMetrics;
  label: string;
  description: string;
  format?: 'number' | 'currency' | 'percentage';
}

type MetricComparisonRowDefinition =
  | TrackedMetricComparisonRowDefinition
  | AllTimeMetricComparisonRowDefinition;

interface MetricComparisonGroup {
  title: string;
  rows: MetricComparisonRowDefinition[];
}

function ProfileValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-700">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

function MetricComparisonRow({
  row,
  periodValue,
  allTimeValue,
  allTimeSelected,
}: {
  row: MetricComparisonRowDefinition;
  periodValue?: number;
  allTimeValue?: number;
  allTimeSelected: boolean;
}) {
  return (
    <div
      className={`grid min-h-12 items-center border-b border-gray-200 px-5 py-3 text-sm last:border-b-0 ${
        allTimeSelected
          ? 'grid-cols-[minmax(0,1fr)_130px]'
          : 'grid-cols-[minmax(0,1fr)_130px_130px]'
      }`}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="w-fit cursor-help text-gray-700">{row.label}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{row.description}</p>
        </TooltipContent>
      </Tooltip>
      {!allTimeSelected && (
        <span className="text-right font-medium text-gray-900">
          {formatPeriodMetric(row, periodValue)}
        </span>
      )}
      <span className="text-right text-gray-700">{formatPeriodMetric(row, allTimeValue)}</span>
    </div>
  );
}

function formatPeriodMetric(row: MetricComparisonRowDefinition, value?: number): string | number {
  if (value === undefined) return '—';
  if (row.type === 'tracked') {
    return row.key === 'activeSeconds' ? formatDuration(value) : value.toLocaleString();
  }
  return formatMetricValue(value, row.format).formattedValue;
}

function usageMetricRow(key: keyof UsageMetrics): AllTimeMetricComparisonRowDefinition {
  const metric = ENHANCED_USAGE_METRICS.find(candidate => candidate.key === key);
  if (!metric) {
    throw new Error(`Missing usage metric definition for ${key}`);
  }
  return {
    type: 'allTime',
    key,
    label: metric.label,
    description: metric.description ?? '',
    format: metric.format,
  };
}

function advisorInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
}

function clientsCreated(advisor: WizardAdvisorUsageData): number {
  return (
    advisor.usageMetrics.totalInvitedClients + advisor.usageMetrics.totalClientsWithSurveysCompleted
  );
}

function datePresetLabel(dateFilter: DateFilterState): string {
  switch (dateFilter.preset) {
    case 'allTime':
      return 'All time';
    case 'last7days':
      return 'Last 7 days';
    case 'last30days':
      return 'Last 30 days';
    case 'last90days':
      return 'Last 90 days';
    case 'thisYear':
      return 'This year';
    case 'custom':
      return `${format(dateFilter.startDate, 'MMM d, yyyy')} to ${format(
        dateFilter.endDate,
        'MMM d, yyyy',
      )}`;
  }
}
